/** Pure derivations for F1: band progress, the lifecycle strip, band actions, card pipelines and close-dialog wording (screens §F1). */
import type { BoardTeamRow, CohortBoard, RoundSummary } from "@bsim/contracts";
import { callRequiredIn } from "@bsim/domain";

type RoundState = RoundSummary["state"];

export const STATE_WORDS: Readonly<Record<RoundState, string>> = {
  // The room view has no surprise status, so awaitingSurprise reads true whether the surprise is held or live.
  draft: "not open yet", open: "open", locked: "closed", resolving: "running the year", awaitingSurprise: "paused for a surprise",
  resolvingPhase2: "finishing the year", resolved: "results ready", released: "released", paused: "paused",
};

export const humanRows = (rows: readonly BoardTeamRow[]): BoardTeamRow[] => rows.filter((r) => !r.isAi);
export const aiRows = (rows: readonly BoardTeamRow[]): BoardTeamRow[] => rows.filter((r) => r.isAi);

/**
 * The template's planned surprise is shown from any earlier round ("planned for round N") and is on offer to hold from the round it
 * falls in being open until it is held (ADR-0065); null once held, or once its round is past.
 */
export function plannedSurpriseOffer(board: CohortBoard, round: RoundSummary | null): NonNullable<CohortBoard["plannedSurprise"]> | null {
  const planned = board.plannedSurprise;
  if (planned == null || board.surprise !== null || round === null || planned.roundNo < round.roundNo) return null;
  if (planned.roundNo > round.roundNo) return planned;
  // Its own round before it opens (draft) still shows it, so the strip and the queue never lose it between one round's release and the next open.
  return round.state === "draft" || round.state === "open" || round.state === "locked" || round.state === "paused" ? planned : null;
}

/** Hold is offered only in the planned surprise's own round; before that it is only shown. */
export const plannedIsHoldable = (planned: { readonly roundNo: number }, round: RoundSummary | null): boolean => round !== null && planned.roundNo === round.roundNo && round.state !== "draft";

/** A released surprise the teams can see and are answering (or have answered): delivered, or carrying a response countdown. The projector may show it from then on, never while it is only held. */
export const surpriseLive = (surprise: CohortBoard["surprise"]): boolean =>
  surprise !== null && (surprise.status === "delivered" || (surprise.responseCountdownEndsAt ?? null) !== null);

/** The api accepts a free hold (HOLD_SURPRISE) in draft, open or locked while no surprise is held (the machine's `noSurpriseHeld` guard); the planned strip's own Hold button is the narrower `plannedIsHoldable`. */
export const surpriseHoldable = (surprise: CohortBoard["surprise"], round: RoundSummary | null): boolean =>
  surprise === null && (round?.state === "draft" || round?.state === "open" || round?.state === "locked");

/** A held surprise is ready to release only once the year has stopped at its month (the round is waiting on the surprise). */
export const surpriseReleasable = (surprise: CohortBoard["surprise"], round: RoundSummary | null): boolean =>
  surprise !== null && surprise.status === "held" && round?.state === "awaitingSurprise";

/**
 * A team whose risk officer has no session: a CEO who can sign alone (the plan then reads unattested) or who is waiting on someone who cannot get in; the roster's seat PIN is the way in.
 * It shows from the moment strategies are set in round 1's draft, through the open round, and stays once the CEO has signed unattested (the facilitator still sees why the board says so).
 * Needs the api's `seatsSignedIn` (absent = not reported = never says so); a team with no risk officer at all is the roles item's, not this one.
 * Contract wall: the board row knows only live sessions, so it never says "never signed in" (that needs `BoardTeamRow.seatsEverSignedIn`); the team's own sign box, which reads `everSignedIn`, says which case it is.
 */
export const croNotSignedIn = (row: BoardTeamRow, round: RoundSummary | null): boolean => {
  if (round === null || row.isAi || row.seatsSignedIn === undefined || row.missingRoles.includes("cro") || row.seatsSignedIn.includes("cro") || row.croAttested) return false;
  if (round.state === "draft") return round.roundNo === 1 && row.strategySet === true;
  return (round.state === "open" || round.state === "paused") && (row.submittedSeq === null || row.signedUnattested === true);
};

/** What the pre-check item counts: people still to be asked, people called in (this session), people who reached it during a round and wait until it ends. */
export function precheckCounts(userIds: readonly string[], deferredIds: readonly string[], calledIn: readonly string[], inPlay = true): { readonly notSubmitted: number; readonly calledIn: number; readonly deferred: number } {
  const called = userIds.filter((u) => calledIn.includes(u)).length;
  // Deferred only while a round is being played: once it has ended they are simply people still to be asked.
  const deferred = inPlay ? userIds.filter((u) => deferredIds.includes(u) && !calledIn.includes(u)).length : 0;
  return { notSubmitted: userIds.length - called - deferred, calledIn: called, deferred };
}
/** Whether a round is being played (so a pre-check reached during it still waits), rather than between rounds: the server's in-play set, so a round that is results-ready is not. */
export const roundInPlay = (round: RoundSummary | null): boolean => round !== null && (round.state === "open" || round.state === "paused" || round.state === "locked" || round.state === "awaitingSurprise");

/** `inPlay`: the deferred wait until the round ends; once it has, the pre-check is open to them and the words say so. */
export const precheckWords = (c: ReturnType<typeof precheckCounts>, inPlay = true): string =>
  [`${c.notSubmitted} not submitted`, c.calledIn === 0 ? null : `${c.calledIn} called in`, c.deferred === 0 ? null : inPlay ? `${c.deferred} deferred until the round ends` : `${c.deferred} reached it during a round; it is open to them now`].filter((t) => t !== null).join(" \u00b7 ");

/** The round the band shows: the current one, else the latest that exists. */
export const bandRound = (board: CohortBoard): RoundSummary | null =>
  board.rounds.find((r) => r.id === board.currentRoundId) ?? board.rounds.at(-1) ?? null;

export interface BandProgress { readonly detail?: string; readonly key: string; readonly label: string; readonly done: number; readonly total: number; readonly tone: "onNavy" | "good"; readonly empty?: string }

/** An AI bank is ready once the round has started (its plan is built at lock, not by a person) or it has a submission: the one test for the strip and the market table. */
export const aiReady = (row: BoardTeamRow, round: RoundSummary | null): boolean => (round !== null && round.state !== "draft") || row.submittedSeq !== null;

/**
 * "CRO attested": a risk officer's decline or objection that the CEO signed over is not a plain attestation, so it is counted apart ("1 attested · 1 signed over the risk officer's decline or objection"). The board row carries no stance, only that the CEO signed over the
 * risk officer's objection or decline (`ceoOverrodeObjection`), which is what the line names.
 */
function croBar(humans: readonly BoardTeamRow[]): BandProgress {
  const answered = humans.filter((r) => r.croAttested);
  const over = answered.filter((r) => r.ceoOverrodeObjection === true).length;
  return { key: "cro", label: "Risk officer reviewed", done: answered.length - over, total: humans.length, tone: "onNavy" };
}

/**
 * The six bars: five over human teams, then AI banks ready.
 * "AI banks ready": an AI seat's plan is its persona policy, built at LOCK without anyone's input, so it is ready once the round is
 * open (or once it has a submission).
 */
export function bandProgress(rows: readonly BoardTeamRow[], round: RoundSummary | null): BandProgress[] {
  const humans = humanRows(rows);
  // Nothing is missing or pending before a team has a draft, but nothing is complete either: only a started round and a team past "not started" count.
  const started = round !== null && round.state !== "draft";
  const facingEvents = humans.filter((r) => r.events.length > 0);
  const ai = aiRows(rows);
  const count = (list: readonly BoardTeamRow[], test: (r: BoardTeamRow) => boolean) => list.filter(test).length;
  const bar = (key: string, label: string, done: number, total: number, tone: BandProgress["tone"] = "onNavy"): BandProgress => ({ key, label, done, total, tone });
  // No call in round 1 (ADR-0040): its bar is left out rather than reading 0 of n.
  const call = round !== null && callRequiredIn(round.roundNo) ? [bar("call", "Forecast made", count(humans, (r) => r.forecastState === "committed"), humans.length)] : [];
  return [
    ...call,
    bar("rationales", "Rationales complete", count(humans, (r) => started && r.pipelineStage !== "notStarted" && r.missingRationales === 0), humans.length),
    { ...bar("events", "Events answered", count(facingEvents, (r) => started && r.pendingEventResponses === 0), facingEvents.length), empty: "None this round" },
    croBar(humans),
    bar("ceo", "CEO signed", count(humans, (r) => r.closeFallback === "signed"), humans.length),
    bar("ai", "AI banks ready", count(ai, (r) => aiReady(r, round)), ai.length, "good"),
  ];
}

export interface LifecycleStep { readonly key: string; readonly label: string; readonly status: "done" | "now" | "todo" }

/** Open → Deciding → Closed → Run to m{n} → Surprise live → Finish year → Release; without a held surprise the middle three collapse to "Run the year". */
export function lifecycle(round: RoundSummary | null, surprise: CohortBoard["surprise"]): LifecycleStep[] {
  const state: RoundState = round?.state === "paused" ? (round.pausedFrom ?? "open") : (round?.state ?? "draft");
  const steps: readonly (readonly [string, string])[] = surprise === null
    ? [["open", "Open"], ["deciding", "Deciding"], ["closed", "Closed"], ["run", "Run the year"], ["release", "Release"]]
    : [["open", "Open"], ["deciding", "Deciding"], ["closed", "Closed"], ["run", `Run to month ${surprise.month}`], ["surprise", `Year stopped at month ${surprise.month}`], ["finish", "Finish year"], ["release", "Release"]];
  const at: Readonly<Record<RoundState, string>> = {
    draft: "", open: "deciding", locked: "closed", resolving: "run", awaitingSurprise: surprise === null ? "run" : "surprise",
    resolvingPhase2: surprise === null ? "run" : "finish", resolved: "release", released: "release", paused: "deciding",
  };
  const now = steps.findIndex(([key]) => key === at[state]);
  const past = state === "released" ? steps.length : now < 0 ? 0 : now;
  return steps.map(([key, label], i) => ({ key, label, status: i < past ? "done" : i === now ? "now" : "todo" }));
}

export type BandActionKey =
  | "open" | "close" | "run" | "reopen" | "releaseSurprise" | "continueYear" | "releaseResults" | "studio" | "openNext"
  | "resume" | "pause" | "countdown" | "inject" | "hold" | "danger" | "stuck";
export interface BandActions { readonly primary: BandActionKey | null; readonly secondary: readonly BandActionKey[] }

/** The action table of screens §F1, by `RoundSummary.state`; after the last round is released there is no next round to open. */
export function bandActions(state: RoundState, hasNext = true, surpriseReleased = false): BandActions {
  switch (state) {
    case "draft": return { primary: "open", secondary: ["hold", "inject"] };
    case "open": return { primary: "close", secondary: ["pause", "countdown", "inject"] };
    case "locked": return { primary: "run", secondary: ["reopen"] };
    case "resolving": case "resolvingPhase2": return { primary: null, secondary: ["stuck"] };
    // After the release the way on is "Continue the year…"; before it, releasing is the primary action.
    case "awaitingSurprise": return surpriseReleased ? { primary: "continueYear", secondary: ["pause", "countdown"] } : { primary: "releaseSurprise", secondary: ["continueYear", "pause", "countdown"] };
    case "resolved": return { primary: "releaseResults", secondary: ["danger"] };
    case "released": return { primary: "studio", secondary: hasNext ? ["openNext"] : [] };
    case "paused": return { primary: "resume", secondary: [] };
  }
}

export type StepState = "done" | "active" | "warn" | "none";

/** Call · Levers · Why · CRO · CEO (screens §F1 card). */
export function pipelineSteps(r: BoardTeamRow, roundNo: number): readonly { readonly label: string; readonly state: StepState }[] {
  const changed = r.changedFamilies > 0;
  // A signed plan with nothing changed is a decision too ("no changes"); the Why bar then follows the rationale counter, never empty beside "2 of 2".
  const signed = r.submittedSeq !== null || r.pipelineStage === "signed";
  return [
    ...(callRequiredIn(roundNo) ? [{ label: "Call", state: r.forecastState === "committed" ? "done" as const : "none" as const }] : []),
    { label: "Decisions", state: changed || signed ? "done" : "none" },
    { label: "Why", state: changed || signed ? (r.missingRationales === 0 ? "done" : "active") : "none" },
    { label: "Risk officer", state: r.croAttested ? "done" : r.openObjections > 0 ? "warn" : r.pipelineStage === "withCro" ? "active" : "none" },
    { label: "CEO", state: r.submittedSeq !== null ? "done" : r.croAttested ? "active" : "none" },
  ];
}

export const isAlert = (r: BoardTeamRow): boolean => r.lowEngagement || r.missingRoles.length > 0;
export const needsAttention = (r: BoardTeamRow): boolean => r.openObjections > 0 || r.pendingExceptions > 0;

/** What goes in if the round closes now (OD4-7); R1 falls back to the standing plan. Parts are lead · bold · tail. */
export function fallbackWords(r: BoardTeamRow, roundNo: number, startsSentence = false, closed = false): { readonly lead: string; readonly strong: string; readonly tail: string } {
  const goes = closed ? "went in" : "goes in";
  const plan = roundNo === 1 ? "your status-quo plan" : "last year's plan";
  switch (r.closeFallback) {
    case "signed": return closed
      ? { lead: "", strong: `Signed${r.draftVersion === null ? "" : ` v${r.draftVersion}`}`, tail: `went in${r.signedUnattested === true ? ", without the risk officer's review" : ""}` }
      : { lead: r.signedUnattested === true ? "CEO signed unattested" : "CEO signed", strong: r.draftVersion === null ? "" : `v${r.draftVersion}`, tail: "" };
    case "lastAttested": {
      const strong = r.attestedVersion === null ? "attested version" : `attested v${r.attestedVersion}`;
      return { lead: "", strong: startsSentence ? strong.charAt(0).toUpperCase() + strong.slice(1) : strong, tail: goes };
    }
    default: return { lead: "No attested plan:", strong: plan, tail: goes };
  }
}

/** The close sources a card flags ("Rescued", "Last attested", "Last year's plan"). */
export const SOURCE_WORDS: Readonly<Record<string, string>> = { facilitator_rescue: "Rescued", deadline_attested: "Last attested", default_carry_forward: "Last year's plan" };

/** Alerts first, then attention, then headline rank; or headline rank alone. */
export function sortTeams(rows: readonly BoardTeamRow[], mode: "attention" | "rank", rankOf: (r: BoardTeamRow) => number | null): BoardTeamRow[] {
  const weight = (r: BoardTeamRow): number => (mode === "rank" ? 0 : isAlert(r) ? 0 : needsAttention(r) ? 1 : 2);
  return [...rows].sort((a, b) => weight(a) - weight(b) || (rankOf(a) ?? 99) - (rankOf(b) ?? 99));
}

export const PIPELINE_WORDS: Readonly<Record<NonNullable<BoardTeamRow["pipelineStage"]>, string>> = {
  notStarted: "Not started", forecastCommitted: "Forecast made", drafting: "Deciding", withCro: "With the risk officer", signed: "Signed",
};

/** What the surprise bar and the preview call the round's state when a surprise is the reason: held until released, live until the clock ends. */
export const stateWords = (round: RoundSummary, surprise: CohortBoard["surprise"]): string => {
  if (round.state !== "awaitingSurprise") return STATE_WORDS[round.state];
  if (surprise?.status === "held") return "surprise held, not yet released";
  return "surprise live until the clock ends";
};

/** The ranking mode, read-only (screens §F1); blend cohorts name their weights (content §2.5). */
export const rankingWords = (ranking: CohortBoard["cohort"]["ranking"]): string =>
  ranking.mode === "valueVsTwin"
    ? "Ranked by value vs each twin"
    : `Ranked by blend · value ${ranking.weights.value}% · customers ${ranking.weights.customers}% · employees ${ranking.weights.employees}% · regulators ${ranking.weights.regulators}%`;

/** The scenario as the top bar says it: "higher-for-longer" → "Higher for longer". */
export const scenarioWords = (scenarioId: string): string => {
  const words = scenarioId.replaceAll("-", " ");
  return words.charAt(0).toUpperCase() + words.slice(1);
};

/**
 * The event line of a card: the card's title, then "option B", "took the default" or "not answered yet".
 * The option reads "option B: <label>" when the card list carries labels, the letter alone when it does not.
 */
export const eventWords = (e: BoardTeamRow["events"][number], label?: string): string =>
  e.state === "defaulted" || e.choiceId === "default" ? "took the default" : e.choiceId === null ? "not answered yet" : label === undefined ? `option ${e.choiceId}` : `option ${e.choiceId}: ${label}`;
